import type React from 'react'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import DefaultModal from '../../../../../components/DefaultModal'
import PageWrapper from '../../../../../components/Pagewrapper'
import {
  useGetAllBankAccountsByUserIdQuery,
  useGetAllMembersByOrganizationIdQuery,
  useGetAllSwapsByIdQuery,
  useGetAllTransferActivityQuery,
  useGetAllWalletsByUserIdQuery,
  useGetFundDepositsQuery,
  useGetUserQuery,
  useStartImpersonationMutation,
} from '../../../../../redux/services/coreApi'
import { startImpersonationSession } from '../../../../../redux/slices/sessionSlice'
import type { RootState } from '../../../../../redux/store'
import type { PageTab } from '../../../../../types/internal-types'
import { isAdmin } from '../../../../../utils/roleUtils'
import Activity from './components/Activity'
import BankAccounts from './components/BankAccounts'
import Compliance from './components/Compliance'
import FundDeposits from './components/FundDeposits'
import KYC from './components/KYC'
import Members from './components/Members'
import OkxIntegration from './components/OkxIntegration'
import Transfers from './components/Transfers'
import UserDetails from './components/UserDetails'
import Wallets from './components/Wallets'

type ClientScreenProps = {}

const ClientScreen: React.FC<ClientScreenProps> = () => {
  const history = useHistory()
  const dispatch = useDispatch()

  const { id } = useParams<{ id: string }>()
  const sessionRole = useSelector((state: RootState) => state.session.role)
  const isImpersonating = useSelector(
    (state: RootState) => state.session.isImpersonating
  )
  const [startImpersonation, { isLoading: isStartingImpersonation }] =
    useStartImpersonationMutation()
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false)
  const [impersonationReason, setImpersonationReason] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [reasonError, setReasonError] = useState('')

  const { data: client, isLoading: isLoadingUser } = useGetUserQuery(id)
  const { data: members, isLoading: isLoadingMembers } =
    useGetAllMembersByOrganizationIdQuery(
      { organizationId: id },
      { skip: !client || client.role !== 'Organization' }
    )

  const { data: bankAccounts, isLoading: isLoadingBankAccounts } =
    useGetAllBankAccountsByUserIdQuery({ userId: id })
  const { data: wallets, isLoading: isLoadingWallets } =
    useGetAllWalletsByUserIdQuery({ userId: id })
  const { data: swaps, isLoading: isLoadingSwaps } = useGetAllSwapsByIdQuery({
    userId: id,
  })
  const { data: activity, isLoading: isLoadingActivity } =
    useGetAllTransferActivityQuery({ userId: id })
  const { data: fundDeposits, isLoading: isLoadingFundDeposits } =
    useGetFundDepositsQuery({ investorId: id })

  const isLoading =
    isLoadingUser ||
    isLoadingBankAccounts ||
    isLoadingWallets ||
    isLoadingMembers ||
    isLoadingActivity ||
    isLoadingSwaps ||
    isLoadingFundDeposits

  const [tab, setTab] = useState<
    | 'user-details'
    | 'kyb/kyc'
    | 'compliance'
    | 'members'
    | 'activity'
    | 'transfers'
    | 'bank-accounts'
    | 'wallets'
    | 'fund-deposits'
    | 'okx-integration'
  >('user-details')

  const tabs: PageTab[] = [
    {
      title: 'User details',
      active: tab === 'user-details',
      onPress: () => setTab('user-details'),
    },
    {
      title: 'Bank accounts',
      active: tab === 'bank-accounts',
      onPress: () => setTab('bank-accounts'),
      amount: `${bankAccounts?.length}`,
    },
    {
      title: 'Wallets',
      active: tab === 'wallets',
      onPress: () => setTab('wallets'),
      amount: `${wallets?.length}`,
    },
    {
      title: client?.role === 'Organization' ? 'KYB' : 'KYC',
      active: tab === 'kyb/kyc',
      onPress: () => setTab('kyb/kyc'),
      notification: client?.kycDetails?.kycStatus === 'Completed',
    },
    {
      title: 'Compliance',
      active: tab === 'compliance',
      onPress: () => setTab('compliance'),
    },
    {
      title: 'OKX Integration',
      active: tab === 'okx-integration',
      onPress: () => setTab('okx-integration'),
    },
    {
      title: 'OTC Transfers',
      active: tab === 'transfers',
      onPress: () => setTab('transfers'),
      amount: `${swaps?.length}`,
    },
    {
      title: 'Fund Deposits',
      active: tab === 'fund-deposits',
      onPress: () => setTab('fund-deposits'),
      amount: `${fundDeposits?.length}`,
    },
    ...(client?.role === 'Organization'
      ? [
          {
            title: 'Members',
            active: tab === 'members',
            onPress: () => setTab('members'),
            amount: members?.length,
          } as PageTab,
        ]
      : []),
    {
      title: 'Activity',
      active: tab === 'activity',
      onPress: () => setTab('activity'),
      amount: `${activity?.length}`,
    },
  ]

  const canImpersonate =
    isAdmin(sessionRole) &&
    !isImpersonating &&
    client &&
    (client.role === 'Individual' ||
      client.role === 'Organization' ||
      client.role === 'Member')

  const openReasonModal = () => {
    setReasonError('')
    setIsReasonModalOpen(true)
  }

  const closeReasonModal = () => {
    if (isStartingImpersonation) return
    setIsReasonModalOpen(false)
    setImpersonationReason('')
    setTotpCode('')
    setReasonError('')
  }

  const handleStartImpersonation = async () => {
    if (!client) return
    const reason = impersonationReason.trim()
    if (reason.length < 10 || reason.length > 500) {
      setReasonError('Reason must be between 10 and 500 characters.')
      return
    }
    if (!totpCode.trim()) {
      setReasonError('Please enter your authenticator code.')
      return
    }

    try {
      const response = await startImpersonation({
        targetUserId: client.id,
        reason,
        totpCode: totpCode.trim(),
      }).unwrap()

      dispatch(
        startImpersonationSession({
          impersonationToken: response.token,
          targetUserId: response.targetUserId,
          targetRole: response.targetRole,
          expiresAtUtc: response.expiresAtUtc,
          returnPath: history.location.pathname,
        })
      )

      closeReasonModal()
      history.push('/app/get-started')
    } catch {
      // Error toasts are handled by RTK Query middleware.
    }
  }

  return (
    <>
      <DefaultModal
        isLoading={isStartingImpersonation}
        isOpen={isReasonModalOpen}
        onClose={closeReasonModal}
        title="Start impersonation"
        isLoadingText="Starting impersonation..."
        whiteBG
      >
        <div className="p-5 pt-0 space-y-3">
          <p className="text-description">
            Please describe why you need to impersonate this user. This reason
            will be stored in the audit trail.
          </p>
          <textarea
            className="input min-h-[120px] max-h-[300px] transition-none pt-2"
            value={impersonationReason}
            onChange={e => {
              setImpersonationReason(e.target.value)
              if (reasonError) setReasonError('')
            }}
            placeholder="Example: Investigating customer report #12345 about missing transfer activity."
            maxLength={500}
            disabled={isStartingImpersonation}
          />
          <div className="flex items-center justify-between">
            <span className="text-description">Minimum 10 characters</span>
            <span className="text-description">
              {impersonationReason.trim().length}/500
            </span>
          </div>

          <div className="pt-2">
            <label className="block text-xs font-semi text-brand-light-text-default mb-1">
              Authenticator code
            </label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              className="input transition-none"
              value={totpCode}
              onChange={e => {
                setTotpCode(e.target.value)
                if (reasonError) setReasonError('')
              }}
              placeholder="Enter your 6-digit code"
              maxLength={6}
              disabled={isStartingImpersonation}
            />
            <p className="text-description mt-1">
              Enter the code from your authenticator app to confirm your
              identity.
            </p>
          </div>

          {reasonError && (
            <div className="rounded-md bg-red-50 border border-solid border-red-200 px-3 py-2">
              <p className="text-red-600 text-xs font-semi">{reasonError}</p>
            </div>
          )}

          <div className="flex gap-2 justify-end pt-2">
            <button
              type="button"
              className="button-secondary"
              onClick={closeReasonModal}
              disabled={isStartingImpersonation}
            >
              Cancel
            </button>
            <button
              type="button"
              className="button"
              onClick={handleStartImpersonation}
              disabled={isStartingImpersonation}
            >
              {isStartingImpersonation ? 'Starting...' : 'Start impersonation'}
            </button>
          </div>
        </div>
      </DefaultModal>

      <PageWrapper
        title={`${client?.name} `}
        goBack={() => history.goBack()}
        isLoading={isLoading}
        tabs={tabs}
        fixedWidth={tab !== 'transfers' && tab !== 'compliance'}
        actions={
          canImpersonate ? (
            <button
              className="flex items-center gap-2 rounded-lg border border-solid border-brand-light-card-border px-3 py-1.5 text-xs font-semi text-brand-light-text-default hover:bg-black/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isStartingImpersonation}
              onClick={openReasonModal}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="w-4 h-4"
              >
                <path d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
                <path
                  fillRule="evenodd"
                  d="M.664 10.59a1.651 1.651 0 0 1 0-1.186A10.004 10.004 0 0 1 10 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0 1 10 17c-4.257 0-7.893-2.66-9.336-6.41ZM14 10a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z"
                  clipRule="evenodd"
                />
              </svg>
              {isStartingImpersonation ? 'Starting...' : 'View as customer'}
            </button>
          ) : undefined
        }
      >
        {tab === 'user-details' && (
          <UserDetails client={client} isLoading={isLoading} />
        )}
        {tab === 'bank-accounts' && (
          <BankAccounts bankAccounts={bankAccounts} />
        )}
        {tab === 'wallets' && <Wallets wallets={wallets} />}
        {tab === 'kyb/kyc' && <KYC client={client} />}
        {tab === 'compliance' && <Compliance userId={id} />}
        {tab === 'transfers' && <Transfers swaps={swaps} />}
        {tab === 'fund-deposits' && <FundDeposits userId={id} />}
        {tab === 'members' && <Members members={members} />}
        {tab === 'activity' && <Activity activity={activity} />}
        {tab === 'okx-integration' && <OkxIntegration client={client} />}
      </PageWrapper>
    </>
  )
}

export default ClientScreen
